// ArChat Layout Fix
// Created: 12-03-2026 17:33:56
// Author: SIVOL® - Aldo Frias - Argentina

import { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { loadHelpTranslations } from '../../utils/translations/help';
import { validateLoadedTranslations } from '../../utils/translations/help/validateTranslations';
import { useTheme } from '../../context/ThemeContext';
import { apiService } from '../../services/api';
import { getTextDirection } from '../../i18n/getTextDirection';
import { SUPPORTED_LANGUAGES_COUNT } from '../../constants/i18n';
import PlanHelp from './PlanHelp';
import { APP_VERSION } from '../../version';
import { BUILD_ID } from '../../config/buildInfo';
import './HelpView.css';

const HelpView = ({ onClose }) => {
  const { currentLanguage, t: appT } = useLanguage();
  const [helpTranslations, setHelpTranslations] = useState({});
  const [loading, setLoading] = useState(true);
  const [limitsFree, setLimitsFree] = useState(null);
  const [limitsPro, setLimitsPro] = useState(null);
  const lastLoadRequestIdRef = useRef(0);
  const { theme: currentTheme } = useTheme();
  const isDark = currentTheme === 'dark';
  const helpDir = getTextDirection(currentLanguage);

  // Cargar traducciones de ayuda (inglés como relleno para claves aún no traducidas en otros idiomas)
  useEffect(() => {
    const loadTranslations = async () => {
      const requestId = lastLoadRequestIdRef.current + 1;
      lastLoadRequestIdRef.current = requestId;
      setLoading(true);
      try {
        const [primary, enBundle] = await Promise.all([
          loadHelpTranslations(currentLanguage),
          loadHelpTranslations('en'),
        ]);
        if (lastLoadRequestIdRef.current !== requestId) {
          return;
        }

        const merged = { ...enBundle };
        Object.keys(primary || {}).forEach((k) => {
          const v = primary[k];
          if (v != null && String(v).trim() !== '') merged[k] = v;
        });

        const isValid = validateLoadedTranslations(merged, currentLanguage);
        if (!isValid) {
          console.error(`❌ [HELP_VIEW] Validación de traducciones FALLIDA para idioma "${currentLanguage}". Revisa el archivo helpTranslations.${currentLanguage}.js`);
        }
        setHelpTranslations(merged);
      } catch (error) {
        if (lastLoadRequestIdRef.current !== requestId) {
          return;
        }
        console.error('❌ [HELP_VIEW] Error loading help translations:', error);
        const fallback = await loadHelpTranslations('es');
        if (lastLoadRequestIdRef.current !== requestId) {
          return;
        }
        setHelpTranslations(fallback);
      } finally {
        if (lastLoadRequestIdRef.current === requestId) {
          setLoading(false);
        }
      }
    };

    loadTranslations();

    return () => {
      lastLoadRequestIdRef.current += 1;
    };
  }, [currentLanguage]);

  // Límites solo desde API. Si falla o no hay limits → null (Ayuda mostrará "No disponible").
  useEffect(() => {
    let cancelled = false;
    const loadLimits = async () => {
      try {
        const [resFree, resPro] = await Promise.all([
          apiService.getPlanInfoByPlan('free'),
          apiService.getPlanInfoByPlan('pro'),
        ]);
        if (cancelled) return;
        const free = resFree?.success && resFree?.data?.limits ? resFree.data.limits : null;
        const pro = resPro?.success && resPro?.data?.limits ? resPro.data.limits : null;
        setLimitsFree(free);
        setLimitsPro(pro);
      } catch {
        if (!cancelled) {
          setLimitsFree(null);
          setLimitsPro(null);
        }
      }
    };
    loadLimits();
    return () => { cancelled = true; };
  }, []);

  /**
   * Función de traducción SEGURA - NUNCA muestra claves
   * @param {string} key - Clave de traducción
   * @returns {string} - Texto traducido o fallback legible
   */
  const t = (key, params = undefined) => {
    if (helpTranslations[key] && typeof helpTranslations[key] === 'string' && helpTranslations[key].trim() !== '') {
      let text = helpTranslations[key];
      if (params && typeof params === 'object') {
        Object.keys(params).forEach((param) => {
          text = String(text).replace(`{${param}}`, params[param]);
        });
      }
      return text;
    }
    return appT(key, params) || '';
  };

  if (loading) {
    return (
      <div className="ayuda-view active">
        <div className="ayuda-container help-view-container" dir={helpDir} data-help-dir={helpDir}>
          <div className="ayuda-header ayuda-header--navbar-title" role="status">
            <span>{appT('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ayuda-view active">
      <div className="ayuda-container help-view-container" dir={helpDir} data-help-dir={helpDir}>
        <div className="ayuda-header ayuda-header--navbar-title">
          <button
            type="button"
            className="help-close-button"
            onClick={() => {
              if (typeof onClose === 'function') {
                onClose();
              }
            }}
            aria-label={appT('common.close')}
          >
            ✕
          </button>
        </div>
        <div className="ayuda-content">
          <section className="ayuda-section">
            <h2>{t('help.overview.title')}</h2>
            <p>{t('help.overview.p1')}</p>
            <p>{t('help.overview.p2')}</p>
          </section>

          <section className="ayuda-section">
            <h2>{t('help.registration.title')}</h2>
            <p>{t('help.registration.p1')}</p>
            <p>{t('help.registration.p2')}</p>
            <p>{t('help.registration.p3')}</p>
            <p>{t('help.registration.p4')}</p>
          </section>

          <section className="ayuda-section">
            <h2>{t('help.invitationsBlock.title')}</h2>
            <p>{t('help.invitationsBlock.p1')}</p>
            <p>{t('help.invitationsBlock.p2')}</p>
            <p>{t('help.invitationsBlock.p3')}</p>
            <p><strong>{t('help.invitationsBlock.p4')}</strong></p>
          </section>

          <section className="ayuda-section">
            <h2>{t('help.languages.title')}</h2>
            <ul className="ayuda-list">
              <li>{t('help.languages.point1', { count: SUPPORTED_LANGUAGES_COUNT })}</li>
              <li>{t('help.languages.point2')}</li>
              <li>{t('help.languages.point3')}</li>
              <li>{t('help.languages.point4')}</li>
            </ul>
            <p>{t('help.languages.extra')}</p>
          </section>

          <section className="ayuda-section">
            <h2>{t('help.messaging.title')}</h2>
            <p>{t('help.messaging.p1')}</p>
            <p>{t('help.messaging.p2')}</p>
            <p>{t('help.messaging.p3')}</p>
            <p>{t('help.messaging.p4')}</p>
            <p>{t('help.messaging.p5')}</p>
            <h3 className="ayuda-h3-inline">{t('help.messaging.timeSubtitle')}</h3>
            <p>{t('help.messaging.time1')}</p>
            <p>{t('help.messaging.time2')}</p>
            <p>{t('help.messaging.time3')}</p>
            <p className="ayuda-summary">{t('help.messaging.timeSummary')}</p>
            {helpTranslations['help.messaging.subjectsSubtitle'] ? (
              <>
                <h3 className="ayuda-h3-inline">{t('help.messaging.subjectsSubtitle')}</h3>
                <p>{t('help.messaging.subjects.p1')}</p>
                <p>{t('help.messaging.subjects.p2')}</p>
                <p>{t('help.messaging.subjects.p3')}</p>
                <p>{t('help.messaging.subjects.p4')}</p>
                <p>{t('help.messaging.subjects.p5')}</p>
              </>
            ) : null}
            {helpTranslations['help.messaging.templatesSubtitle'] ? (
              <>
                <h3 className="ayuda-h3-inline">{t('help.messaging.templatesSubtitle')}</h3>
                <p>{t('help.messaging.templates.p1')}</p>
                <p>{t('help.messaging.templates.p2')}</p>
                <p>{t('help.messaging.templates.p3')}</p>
                <p>{t('help.messaging.templates.p4')}</p>
                <p>{t('help.messaging.templates.p5')}</p>
                <p>{t('help.messaging.templates.p6')}</p>
                <p>{t('help.messaging.templates.p7')}</p>
              </>
            ) : null}
          </section>

          <section className="ayuda-section">
            <h2>{t('help.files.title')}</h2>
            <p>{t('help.files.point1')}</p>
            <p>{t('help.files.point2')}</p>
            <p>{t('help.files.point3')}</p>
            <p>{t('help.files.point4')}</p>
            <h3 className="ayuda-h3-inline">{t('help.myFiles.title')}</h3>
            <p>{t('help.myFiles.p1')}</p>
            <p>{t('help.myFiles.p2')}</p>
            <p>{t('help.myFiles.p3')}</p>
            <p>{t('help.myFiles.p4')}</p>
            <h3 className="ayuda-h3-inline">{t('help.files.captureTitle')}</h3>
            <p>{t('help.files.point2b')}</p>
            <p>{t('help.files.point3b')}</p>
            <p>{t('help.files.point4b')}</p>
            <p>{t('help.files.point5b')}</p>
          </section>

          <section className="ayuda-section">
            <h2>{t('help.dashboardSection.title')}</h2>
            <p>{t('help.dashboardSection.p1')}</p>
            <ul className="ayuda-list">
              <li>{t('help.dashboardSection.li1')}</li>
              <li>{t('help.dashboardSection.li2')}</li>
              <li>{t('help.dashboardSection.li3')}</li>
              <li>{t('help.dashboardSection.li4')}</li>
              <li>{t('help.dashboardSection.li5')}</li>
              {helpTranslations['help.dashboardSection.li6'] ? (
                <li>{t('help.dashboardSection.li6')}</li>
              ) : null}
            </ul>
            <p>{t('help.dashboardSection.p2')}</p>
            <p>{t('help.dashboardSection.p3')}</p>
            {helpTranslations['help.dashboardSection.p4'] ? (
              <p>{t('help.dashboardSection.p4')}</p>
            ) : null}
          </section>

          <section className="ayuda-section">
            <h2>{t('help.settingsSection.title')}</h2>
            <p>{t('help.settingsSection.p1')}</p>
            <ul className="ayuda-list">
              <li>{t('help.settingsSection.li1')}</li>
              <li>{t('help.settingsSection.li2')}</li>
              <li>{t('help.settingsSection.li3')}</li>
              <li>{t('help.settingsSection.li4')}</li>
              <li>{t('help.settingsSection.li5')}</li>
              <li>{t('help.settingsSection.li6')}</li>
              {helpTranslations['help.settingsSection.li7'] ? (
                <li>{t('help.settingsSection.li7')}</li>
              ) : null}
              {helpTranslations['help.settingsSection.li8'] ? (
                <li>{t('help.settingsSection.li8')}</li>
              ) : null}
            </ul>
            <p>{t('help.settingsSection.p2')}</p>
            {helpTranslations['help.settingsSection.p3'] ? (
              <p>{t('help.settingsSection.p3')}</p>
            ) : null}
            {helpTranslations['help.settingsSection.p4'] ? (
              <p>{t('help.settingsSection.p4')}</p>
            ) : null}
          </section>

          <section className="ayuda-section">
            <h2>{t('help.privacy.title')}</h2>
            <p>{t('help.privacy.point1')}</p>
            <p>{t('help.privacy.point2')}</p>
            <p>{t('help.privacy.point3')}</p>
            <p>{t('help.privacy.point4')}</p>
            <ul className="ayuda-list">
              <li>{t('help.privacy.point5')}</li>
              <li>{t('help.privacy.point6')}</li>
              <li>{t('help.privacy.point7')}</li>
              <li>{t('help.privacy.point8')}</li>
            </ul>
            <p>{t('help.privacy.point9')}</p>
          </section>

          <section className="ayuda-section">
            <h2>{t('help.sessionsSection.title')}</h2>
            <p>{t('help.sessionsSection.p1')}</p>
            <p>{t('help.sessionsSection.p2')}</p>
            <p>{t('help.sessionsSection.p3')}</p>
            <p>{t('help.sessionsSection.p4')}</p>
          </section>

          {loading ? (
            <section className="ayuda-section">
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <p>{appT('common.loading')}</p>
              </div>
            </section>
          ) : (
            <PlanHelp helpTranslations={helpTranslations} limitsFree={limitsFree} limitsPro={limitsPro} />
          )}

          <section className="ayuda-section">
            <h2>{t('help.promotionsSection.title')}</h2>
            <p>{t('help.promotionsSection.p1')}</p>
            <p>{t('help.promotionsSection.p2')}</p>
            <p>{t('help.promotionsSection.p3')}</p>
          </section>

          <section className="ayuda-section">
            <h2>{t('help.ratingsSection.title')}</h2>
            <p>{t('help.ratingsSection.p1')}</p>
            <p>{t('help.ratingsSection.p2')}</p>
            <p>{t('help.ratingsSection.p3')}</p>
            <h3 className="ayuda-h3-inline">{t('help.suggestions.title')}</h3>
            <p>{t('help.suggestions.point1')}</p>
            <p>{t('help.suggestions.point2')}</p>
          </section>

          <section className="ayuda-section">
            <h2>{t('help.exportSection.title')}</h2>
            <p>{t('help.exportSection.p1')}</p>
            <ul className="ayuda-list">
              <li>{t('help.exportSection.li1')}</li>
              <li>{t('help.exportSection.li2')}</li>
            </ul>
            <p>{t('help.exportSection.p2')}</p>
            <p><strong>{t('help.exportSection.p3')}</strong></p>
          </section>

          <section className="ayuda-section">
            <h2>{t('help.tipsNew.title')}</h2>
            <ul className="ayuda-list">
              <li>{t('help.tipsNew.point1')}</li>
              <li>{t('help.tipsNew.point2')}</li>
              <li>{t('help.tipsNew.point3')}</li>
              <li>{t('help.tipsNew.point4')}</li>
              <li>{t('help.tipsNew.point5')}</li>
              <li>{t('help.tipsNew.point6')}</li>
              {helpTranslations['help.tipsNew.point7'] ? (
                <li>{t('help.tipsNew.point7')}</li>
              ) : null}
              {helpTranslations['help.tipsNew.point8'] ? (
                <li>{t('help.tipsNew.point8')}</li>
              ) : null}
            </ul>
            <h3 className="ayuda-h3-inline">{t('help.experience.title')}</h3>
            <ul className="ayuda-list">
              <li>{t('help.experience.point1')}</li>
              <li>{t('help.experience.point2')}</li>
              <li>{t('help.experience.point3')}</li>
              <li>{t('help.experience.point4')}</li>
              <li>{t('help.experience.point5')}</li>
              <li>{t('help.experience.point6')}</li>
              {helpTranslations['help.experience.point7'] ? (
                <li>{t('help.experience.point7')}</li>
              ) : null}
            </ul>
          </section>

          {helpTranslations['help.tags.title'] ? (
            <section className="ayuda-section">
              <h2>{t('help.tags.title')}</h2>
              <p>{t('help.tags.p1')}</p>
              <p>{t('help.tags.p2')}</p>
              <p>{t('help.tags.p3')}</p>
              <p>{t('help.tags.p4')}</p>
              <p>{t('help.tags.p5')}</p>
              <p>{t('help.tags.p6')}</p>
            </section>
          ) : null}

          {helpTranslations['help.faq.title'] ? (
            <section className="ayuda-section">
              <h2>{t('help.faq.title')}</h2>
              <h3>{t('help.faq.q1')}</h3>
              <p>{t('help.faq.a1')}</p>
              <h3>{t('help.faq.q2')}</h3>
              <p>{t('help.faq.a2')}</p>
              <h3>{t('help.faq.q3')}</h3>
              <p>{t('help.faq.a3')}</p>
              <h3>{t('help.faq.q4')}</h3>
              <p>{t('help.faq.a4')}</p>
              <h3>{t('help.faq.q5')}</h3>
              <p>{t('help.faq.a5')}</p>
              <h3>{t('help.faq.q6')}</h3>
              <p>{t('help.faq.a6')}</p>
              <h3>{t('help.faq.q7')}</h3>
              <p>{t('help.faq.a7')}</p>
              {helpTranslations['help.faq.q8'] ? (
                <>
                  <h3>{t('help.faq.q8')}</h3>
                  <p>{t('help.faq.a8')}</p>
                </>
              ) : null}
              {helpTranslations['help.faq.q9'] ? (
                <>
                  <h3>{t('help.faq.q9')}</h3>
                  <p>{t('help.faq.a9')}</p>
                </>
              ) : null}
              {helpTranslations['help.faq.q10'] ? (
                <>
                  <h3>{t('help.faq.q10')}</h3>
                  <p>{t('help.faq.a10')}</p>
                </>
              ) : null}
            </section>
          ) : null}

          <section className="ayuda-section">
            <h2>{t('help.whySubscribe.title')}</h2>
            <p>{t('help.whySubscribe.point1')}</p>
            <p>{t('help.whySubscribe.point2')}</p>
            <ul className="ayuda-list">
              <li>{t('help.whySubscribe.point3')}</li>
              <li>{t('help.whySubscribe.point4')}</li>
              <li>{t('help.whySubscribe.point5')}</li>
              <li>{t('help.whySubscribe.point6')}</li>
              <li>{t('help.whySubscribe.point7')}</li>
              <li>{t('help.whySubscribe.point8')}</li>
            </ul>
            <p><strong>{t('help.whySubscribe.point9')}</strong></p>
          </section>

          <section className="ayuda-section">
            <h2>{t('help.about.title')}</h2>
            <div className="ayuda-about">
              <p>{t('help.about.developer')}</p>
              <p className="ayuda-about-name">Aldo Frias</p>
              <p className="ayuda-about-location">{t('help.about.location')}</p>
              <p className="ayuda-about-linkedin">
                <a
                  href="https://www.linkedin.com/in/aldo-a-frias-web3/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ayuda-linkedin-link"
                >
                  <span>🔗</span> <span>{t('help.about.linkedin')}</span>
                </a>
              </p>
            </div>
          </section>

          <div className="help-slogan-final">
            <h2>{t('help.slogan.final')}</h2>
          </div>
        </div>
        <div className="ayuda-footer">
          <div className="help-footer-meta">
            <p>
              v{APP_VERSION} · {BUILD_ID}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpView;
